/**
 * View placement data (brief §1.3): which views appear where is a line in each
 * view module's own `attach`, read back through `manage_views list`. This
 * module is the pure selection half — attach matching, path resolution and
 * URL param shaping — so it unit-tests without a router or a frame. The host
 * half (`view-frame.tsx`) mounts whatever these select.
 *
 * Selection is a path, never a query key: `…/-/views/<key>` names a view, and
 * on that path query keys are view params except for the reserved host-owned
 * collection selection, preset and table presentation keys. Those keys stay
 * in the URL across collection tabs but are excluded from guest params.
 */

import {
	COLLECTION_PRESET_QUERY_KEY,
	COLLECTION_TABLE_QUERY_KEY,
} from "@lobu/core/contracts/tools/collection-presentation";
import {
	type AttributeValue,
	COLLECTION_QUERY_KEY,
	type CollectionSelection,
} from "@lobu/core/contracts/tools/collection-selection";
import type {
	ViewAttachment,
	ViewParamDecl,
} from "@lobu/core/contracts/tools/manage-views";
import {
	eventAttachmentsFor,
	hasWorkspaceAttachment,
	matchesCollection,
	matchesRecord,
} from "@lobu/core/contracts/tools/view-attach";
import { VIEW_PATH_MARKER } from "@lobu/core/contracts/tools/view-path";
import { collectionRouteValue } from "@/lib/collection-selection";

export {
	VIEW_PATH_MARKER,
	viewPathSuffix,
} from "@lobu/core/contracts/tools/view-path";

export type ViewAttach = ViewAttachment;

export interface AppliedView {
	key: string;
	name: string;
	/** One line under the name on a record card. */
	description?: string;
	attach: ViewAttach[];
	/** Declared params; those with `filter` join the host filter bar. */
	params?: Record<string, ViewParamDecl>;
	content_hash: string | null;
}

export type ViewParams = Record<string, string | number | boolean>;

/**
 * The identity of one mounted view: the organization that owns it plus its
 * key. Both sides of every mount comparison derive it here rather than
 * comparing the key alone — a view key is only unique within an organization,
 * so a key-only match lets a cached shell survive an org switch while the
 * bridge brokers calls against the new org.
 */
export function viewIdentity(orgSlug: string, viewKey: string): string {
	return `${orgSlug}\u0000${viewKey}`;
}

/** The tabs on a type's list page: views attached to its collection whose
 *  `when` the current selection implies. */
export function viewsForCollection(
	views: AppliedView[],
	entityType: string,
	collection: CollectionSelection = {},
): AppliedView[] {
	return views.filter((v) =>
		v.attach.some((a) => matchesCollection(a, entityType, collection)),
	);
}

/** The cards on one record's page: views attached to its type, plus any pinned
 *  to its id. An unstored source row's placeholder id (0) matches no pin. */
export function viewsForRecord(
	views: AppliedView[],
	record: { type: string; id: number | string | null },
): AppliedView[] {
	const storedRecord = {
		type: record.type,
		id: typeof record.id === "number" ? record.id : 0,
	};
	return views.filter((v) =>
		v.attach.some((a) => matchesRecord(a, storedRecord)),
	);
}

/** Views attached to the workspace: direct workspace navigation destinations. */
export function workspaceViews(views: AppliedView[]): AppliedView[] {
	return views.filter((v) => hasWorkspaceAttachment(v.attach));
}

/**
 * Views that may render one event: those with an event attachment, narrowed to
 * the event's kind when it is known, in key order (the order the event page's
 * tabs and a card click's "first matching view" both use). This is the
 * client-side half of the match only — an event row does not carry the types
 * of the entities it links, so whether one of them is the attachment's `type`
 * is answered by the server's `open_view` (`use-event-views`).
 */
export function eventViewCandidates(
	views: AppliedView[],
	eventKind?: string,
): AppliedView[] {
	return views
		.filter((v) => eventAttachmentsFor(v.attach, eventKind).length > 0)
		.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

/** A `/-/views/<key>` path this page does not attach: a dead link, rendered
 *  as such rather than silently swapped for the page's default content. */
type MissingView = { kind: "missing"; key: string };

type HubViewSelection =
	| { kind: "schema" }
	| { kind: "view"; view: AppliedView }
	| MissingView;

/** Resolve the Data hub from its path: `/data` is Schema, `/data/-/views/<key>`
 *  a workspace-attached view. The two never share a namespace, so a view keyed
 *  `schema` is an ordinary tab. */
export function selectHubView(
	views: AppliedView[],
	viewKey: string | undefined,
): HubViewSelection {
	if (!viewKey) return { kind: "schema" };
	const view = workspaceViews(views).find((v) => v.key === viewKey);
	return view ? { kind: "view", view } : { kind: "missing", key: viewKey };
}

export type RecordShape = "table" | "board" | "graph";

/** A collection tab whose `when` the current selection does not imply: it
 *  exists, so it reads as "not for these filters" rather than a dead link. */
type InapplicableView = {
	kind: "inapplicable";
	view: AppliedView;
	when: Record<string, AttributeValue>;
};

type TypeViewSelection =
	| { kind: "builtin"; mode: RecordShape }
	| { kind: "view"; view: AppliedView }
	| InapplicableView
	| MissingView;

/** The `when` under which `view` is a tab on `entityType`, if it is conditional. */
function conditionalWhen(
	view: AppliedView,
	entityType: string,
): Record<string, AttributeValue> | undefined {
	for (const a of view.attach) {
		if (!("when" in a) || !a.when) continue;
		const implied: CollectionSelection = {
			filters: Object.entries(a.when).map(([field, value]) => ({
				field,
				op: "eq" as const,
				value,
			})),
		};
		if (matchesCollection(a, entityType, implied)) return a.when;
	}
	return undefined;
}

/** Resolve the type page from its path: `/<type>/-/views/<key>` for an
 *  attached view, `/<type>/-/<shape>` for an explicit shape, else the type's
 *  auto-picked default. Board collapse is the caller's (capability gates live
 *  with the schema, not the URL). */
export function selectTypeView(
	views: AppliedView[],
	entityType: string,
	path: { viewKey?: string; shape?: RecordShape },
	defaultMode: RecordShape = "table",
	collection: CollectionSelection = {},
): TypeViewSelection {
	if (path.viewKey) {
		const view = viewsForCollection(views, entityType, collection).find(
			(v) => v.key === path.viewKey,
		);
		if (view) return { kind: "view", view };
		for (const candidate of views) {
			if (candidate.key !== path.viewKey) continue;
			const when = conditionalWhen(candidate, entityType);
			if (when) return { kind: "inapplicable", view: candidate, when };
		}
		return { kind: "missing", key: path.viewKey };
	}
	return { kind: "builtin", mode: path.shape ?? defaultMode };
}

type EventViewSelection<T extends AppliedView> =
	| { kind: "event" }
	| { kind: "view"; view: T }
	| MissingView;

/** Use the first authorized match by default; preserve explicit view URLs. */
export function selectEventView<T extends AppliedView>(
	views: T[],
	viewKey: string | undefined,
): EventViewSelection<T> {
	if (!viewKey)
		return views[0] ? { kind: "view", view: views[0] } : { kind: "event" };
	const view = views.find((v) => v.key === viewKey);
	return view ? { kind: "view", view } : { kind: "missing", key: viewKey };
}

type RecordViewSelection =
	| { kind: "record" }
	| { kind: "view"; view: AppliedView }
	| MissingView;

/** Resolve the record page: `/<type>/<slug>` is the record (cards above its
 *  activity), `/<type>/<slug>/-/views/<key>` one of its views full page. */
export function selectRecordView(
	cards: AppliedView[],
	viewKey: string | undefined,
): RecordViewSelection {
	if (!viewKey) return { kind: "record" };
	const view = cards.find((v) => v.key === viewKey);
	return view ? { kind: "view", view } : { kind: "missing", key: viewKey };
}

/**
 * Split a record splat (`<type>/<slug>[/-/views/<key>]`) into the entity path
 * and the selected view key. Returns null for any other use of the marker, so
 * a malformed suffix is a not-found rather than an entity lookup on `-`.
 */
export function splitRecordViewPath(
	splat: string,
): { entityPath: string; viewKey?: string } | null {
	const segments = splat.split("/");
	const at = segments.indexOf(VIEW_PATH_MARKER);
	if (at === -1) return { entityPath: splat };
	if (at === 0 || segments.length !== at + 3 || segments[at + 1] !== "views")
		return null;
	const viewKey = segments[at + 2];
	if (!viewKey) return null;
	return { entityPath: segments.slice(0, at).join("/"), viewKey };
}

/** True on any `/-/views/<key>` page. */
export function isViewPath(pathname: string): boolean {
	const segments = pathname.split("/");
	const at = segments.indexOf(VIEW_PATH_MARKER);
	return at !== -1 && segments[at + 1] === "views" && !!segments[at + 2];
}

/**
 * The query string of a `/-/views/<key>` page, including the host-owned
 * collection keys. The router decodes only canonical JSON, so an array or object is put
 * back as exactly the text that was in the URL; the guest types each value by
 * its declaration. An empty string is kept: it is a cleared filter, and
 * dropping it would reseed the guest with that param's declared default.
 * Also the routes' `validateSearch`, which would otherwise whitelist them away.
 */
export function viewRouteSearch(search: Record<string, unknown>): ViewParams {
	const out: ViewParams = {};
	for (const [k, v] of Object.entries(search)) {
		if (
			k === COLLECTION_QUERY_KEY ||
			k === COLLECTION_PRESET_QUERY_KEY ||
			k === COLLECTION_TABLE_QUERY_KEY
		) {
			const value = collectionRouteValue(v);
			if (value !== undefined) out[k] = value;
			continue;
		}
		if (
			typeof v === "string" ||
			typeof v === "number" ||
			typeof v === "boolean"
		) {
			out[k] = v;
		} else if (v !== null && typeof v === "object") {
			out[k] = JSON.stringify(v);
		}
	}
	return out;
}

/** Presentation params never include the host's collection selection. */
export function viewUrlParams(search: Record<string, unknown>): ViewParams {
	const out = viewRouteSearch(search);
	delete out[COLLECTION_QUERY_KEY];
	delete out.$preset;
	delete out.$table;
	return out;
}
